import React from 'react';
import { AlertCircle, SkipForward, RefreshCw, FilePlus2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const DuplicateFilenameDialog = ({
  isOpen,
  onClose,
  duplicateFiles,
  onSkip,
  onReplace,
  onAddWithPrefix,
  isProcessing = false
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-yellow-100 rounded-full">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
            </div>
            <DialogTitle className="text-xl">Duplicate Files Detected</DialogTitle>
          </div>
          <DialogDescription className="text-base">
            {duplicateFiles.length} file{duplicateFiles.length > 1 ? 's' : ''} already exist in this gallery. What would you like to do?
          </DialogDescription>
        </DialogHeader>

        {/* List of duplicate files */}
        <div className="max-h-48 overflow-y-auto bg-gray-50 dark:bg-slate-800 rounded-lg p-3 my-4">
          <ul className="space-y-1.5">
            {duplicateFiles.slice(0, 10).map((filename, index) => (
              <li
                key={index}
                className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 px-3 py-2 rounded border border-gray-200 dark:border-slate-600"
              >
                <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                <span className="truncate">{filename}</span>
              </li>
            ))}
            {duplicateFiles.length > 10 && (
              <li className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                ... and {duplicateFiles.length - 10} more
              </li>
            )}
          </ul>
        </div>

        {/* Options explanation */}
        <div className="space-y-2 mb-4">
          <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
            <SkipForward className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-sm text-gray-700 dark:text-gray-200">Skip Duplicates</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Keep existing files, only upload new ones</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <RefreshCw className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-sm text-gray-700 dark:text-gray-200">Replace All</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Delete existing files and upload new versions</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <FilePlus2 className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-sm text-gray-700 dark:text-gray-200">Keep Both</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Rename new files with a number suffix (e.g., photo_1.jpg)</p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
            className="sm:flex-none"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <div className="flex gap-2 flex-1">
            <Button
              variant="outline"
              onClick={onSkip}
              disabled={isProcessing}
              className="flex-1 gap-2"
            >
              <SkipForward className="w-4 h-4" />
              Skip
            </Button>
            <Button
              variant="outline"
              onClick={onReplace}
              disabled={isProcessing}
              className="flex-1 gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <RefreshCw className={`w-4 h-4 ${isProcessing ? 'animate-spin' : ''}`} />
              Replace
            </Button>
            <Button
              onClick={onAddWithPrefix}
              disabled={isProcessing}
              className="flex-1 gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <FilePlus2 className="w-4 h-4" />
              Keep Both
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
